import { useCallback, useMemo } from 'react';
import { useTargetingContext, useTargetingFieldPath } from '../context/TargetingContext';
import { defaultTargetingBlockingRules, useTargetingDisabledState } from './useTargetingDisabledState';

type UseTargetingOptionDisabledStateResult = {
  getOptionFieldPath: (option?: string) => string | undefined;
  isOptionDisabled: (option?: string) => boolean;
  getOptionDisabledReason: (option?: string) => string | undefined;
  filterEnabledOptions: <T extends string>(options: T[]) => T[];
};

export const useTargetingOptionDisabledState = (parentFieldPath?: string): UseTargetingOptionDisabledStateResult => {
  const targetingContext = useTargetingContext();
  const contextFieldPath = useTargetingFieldPath();
  const effectiveParentFieldPath = parentFieldPath ?? contextFieldPath;

  const targetingBlockingRules = targetingContext?.targetingBlockingRules ?? defaultTargetingBlockingRules;

  const getOptionFieldPath = useCallback(
    (option?: string) => {
      if (!effectiveParentFieldPath || !option) {
        return undefined;
      }

      return `${effectiveParentFieldPath}.${option}`;
    },
    [effectiveParentFieldPath]
  );

  const isOptionDisabled = useCallback(
    (option?: string) => {
      const optionFieldPath = getOptionFieldPath(option);

      return optionFieldPath ? targetingBlockingRules.isFieldDisabled(optionFieldPath) : false;
    },
    [getOptionFieldPath, targetingBlockingRules]
  );

  const getOptionDisabledReason = useCallback(
    (option?: string) => {
      const optionFieldPath = getOptionFieldPath(option);

      return optionFieldPath ? targetingBlockingRules.getDisabledReason(optionFieldPath) : undefined;
    },
    [getOptionFieldPath, targetingBlockingRules]
  );

  const filterEnabledOptions = useCallback(
    <T extends string>(options: T[]) => options.filter(option => !isOptionDisabled(option)),
    [isOptionDisabled]
  );

  return useMemo(
    () => ({
      getOptionFieldPath,
      isOptionDisabled,
      getOptionDisabledReason,
      filterEnabledOptions,
    }),
    [getOptionFieldPath, isOptionDisabled, getOptionDisabledReason, filterEnabledOptions]
  );
};

export const useTargetingOptionDisabledStateForOption = (option?: string, parentFieldPath?: string) => {
  const contextFieldPath = useTargetingFieldPath();
  const effectiveParentFieldPath = parentFieldPath ?? contextFieldPath;
  const optionFieldPath = effectiveParentFieldPath && option ? `${effectiveParentFieldPath}.${option}` : undefined;

  return useTargetingDisabledState(optionFieldPath);
};
